import { tw } from "@careinfo/tailwind";
import {
  File,
  FileArchive,
  FileAudio,
  FileCheck,
  FileImage,
  FileText,
  FileVideo,
  FileX,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import useUploadFileHook from "../../../customHooks/useUploadFileHook";

const Footer = (props: any) => {
  const { handleSend, inputValue, setInputValue, textareaRef, groupId } = props;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const { uploadFile, progress } = useUploadFileHook({
    setIsUploading,
    queryKey: ["file"],
  });

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const generateFileId = () => {
    return (
      "file_" +
      Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("")
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const fileURL = URL.createObjectURL(file);
      setPreviewUrl(fileURL);
      handleUpload(file);
    }
  };

  const handleUpload = async (file: File) => {
    const fileId = generateFileId();
    const res = await uploadFile(file, fileId, groupId);
    setInputValue(res);
  };

  useEffect(() => {
    return () => {
      // Clean up the preview URL on unmount or file change
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const getFileIconComponent = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();

    switch (ext) {
      case "pdf":
      case "doc":
      case "docx":
      case "txt":
        return <FileText className={tw`w-8 h-8 text-blue-600`} />;
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
        return <FileImage className={tw`w-8 h-8 text-green-600`} />;
      case "mp3":
      case "wav":
        return <FileAudio className={tw`w-8 h-8 text-purple-600`} />;
      case "mp4":
      case "avi":
        return <FileVideo className={tw`w-8 h-8 text-pink-600`} />;
      case "zip":
      case "rar":
      case "7z":
        return <FileArchive className={tw`w-8 h-8 text-yellow-600`} />;
      case "json":
        return <FileCheck className={tw`w-8 h-8 text-teal-600`} />;
      case "exe":
        return <FileX className={tw`w-8 h-8 text-red-600`} />;
      default:
        return <File className={tw`w-8 h-8 text-gray-500`} />;
    }
  };

  return (
    <footer className={tw`p-2`}>
      <form
        onSubmit={handleSend}
        className={tw`bg-white rounded-lg flex w-full items-center mb-4 px-2 py-1 justify-between`}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <button
          type="button"
          onClick={handleFileClick}
          className={tw`focus:outline-none text-xl py-1 px-3 text-center rounded-full hover:bg-gray-200 text-gray-500 hover:text-pink-600`}
        >
          +
        </button>
        <div className={tw`flex flex-col w-full`}>
          {previewUrl ? (
            <div className={tw`mb-2 w-fit  relative`}>
              {selectedFile?.type.startsWith("image/") ? (
                <img
                  src={previewUrl}
                  alt="Preview"
                  className={tw`w-20 h-20 object-fit rounded-lg border border-gray-300`}
                />
              ) : (
                <div
                  className={tw`text-sm flex items-center gap-x-2 text-gray-600 bg-gray-100 p-2 rounded-md`}
                >
                  {getFileIconComponent(selectedFile?.name)}
                  {selectedFile?.name} ({Math.round(selectedFile?.size / 1024)}{" "}
                  KB)
                </div>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setInputValue(null);
                  setSelectedFile(null);
                  setPreviewUrl(null);
                }}
                className={tw`absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full w-5 h-5 flex items-center justify-center text-xs text-gray-600 hover:bg-gray-300  transition`}
                aria-label="Remove file"
              >
                ✕
              </button>
            </div>
          ) : (
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  setPreviewUrl(null);
                  handleSend(e);
                }
              }}
              className={tw`p-2 bg-white w-full resize-none overflow-hidden focus:outline-none`}
              placeholder="Type your message..."
              rows={1}
              ref={textareaRef}
            />
          )}
        </div>

        <button
          type="submit"
          onClick={() => setPreviewUrl(null)}
          className={tw`ml-2 focus:outline-none`}
        >
          <svg
            width="26"
            height="23"
            viewBox="0 0 26 23"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.41667 22.3283C1.93056 22.5227 1.46875 22.48 1.03125 22.2C0.59375 21.92 0.375 21.5131 0.375 20.9793V14.4168L12.0417 11.5002L0.375 8.58351V2.02101C0.375 1.48629 0.59375 1.07941 1.03125 0.800386C1.46875 0.521359 1.93056 0.47858 2.41667 0.672053L24.875 10.1512C25.4826 10.4186 25.7865 10.8682 25.7865 11.5002C25.7865 12.1321 25.4826 12.5818 24.875 12.8491L2.41667 22.3283Z"
              fill="#EF0776"
            />
          </svg>
        </button>
      </form>
    </footer>
  );
};

export default Footer;
